import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, XCircle, Flag } from 'lucide-react';
import { UserAvatar } from './UserAvatar';
import { isGuestUsername } from '../../utils/guest';
import PlayerProfileOverlay from '../profile/PlayerProfileOverlay';
import { log } from '../../utils/logger';
import { vibrate } from '../../utils/vibration';
import { isDisplayableRating } from '../../utils/rating';
import './GameEndOverlay.css';
import { useModaleAccessibile } from '../../hooks/useModaleAccessibile';

const RESULT_CONFIG = {
  win: { icon: <CheckCircle size={32} />, className: 'win' },
  loss: { icon: <XCircle size={32} />, className: 'loss' },
  draw: { icon: <Flag size={32} />, className: 'draw' },
};

const RESULT_I18N_KEY = {
  win: 'arena.gameEnd.win',
  loss: 'arena.gameEnd.loss',
  draw: 'arena.gameEnd.draw',
} as const;

interface GameEndOverlayProps {
  resultType: 'win' | 'loss' | 'draw';
  /**
   * Perché la partita è finita, già tradotto (utils/end-reason-text). Arena e torneo
   * mostravano solo vinta/persa/patta: senza questo, un avversario che abbandona o va
   * in tempo è indistinguibile da una partita giocata fino in fondo.
   */
  reasonText?: string;
  opponentUsername: string;
  /** Assente o `null` per chi non ha punteggio (ospiti, bot) o non si conosce. */
  opponentRating?: number | null;
  opponentAvatar?: string | null;
  /** Arena/tournament-specific stats (points breakdown, rank, etc.) */
  statsSlot: ReactNode;
  /** Countdown or waiting message */
  countdownSlot: ReactNode;
  /** Action buttons (analyze, pause, play next, continue) */
  actionsSlot: ReactNode;
}

export default function GameEndOverlay({
  resultType,
  reasonText,
  opponentUsername,
  opponentRating,
  opponentAvatar,
  statsSlot,
  countdownSlot,
  actionsSlot,
}: GameEndOverlayProps) {
  const { t } = useTranslation();
  const config = RESULT_CONFIG[resultType];
  const [showProfile, setShowProfile] = useState(false);
  const titleId = useId();
  const actionsRef = useRef<HTMLDivElement>(null);

  // This overlay has no dismiss of its own and covers the player cards, so the
  // opponent's name here is the only way into their profile between two arena /
  // tournament games. Bots keep their `Guest_` disguise and stay unlinked.
  const canOpenProfile = !isGuestUsername(opponentUsername);

  // Log overlay shown and haptic feedback on mount
  useEffect(() => {
    log.info('GameEndOverlay', 'Overlay shown', { resultType, opponentUsername });
    if (resultType === 'win') {
      vibrate([100, 50, 100]);
    } else {
      vibrate(50);
    }
  }, [resultType]);


  /*
   * Il fuoco: entra sul primo bottone delle AZIONI, e non esce più dal dialogo.
   *
   * Primo bottone delle azioni e non primo del dialogo: quello sarebbe il link al
   * profilo dell'avversario, in mezzo all'intestazione, e chi naviga da tastiera
   * si troverebbe a cercare «rivincita» a tentoni. Da qui il terzo parametro.
   *
   * La trappola (Tab che non esce) è l'altra metà: senza, un Tab di troppo
   * riporta dietro l'overlay — che copre tutto lo schermo — su una damiera e dei
   * bottoni che nessuno vede più.
   *
   * Nessun Escape: questo dialogo non ha una chiusura propria, si esce dalle sue
   * azioni (rivincita, esci), e chiuderlo con un tasto lascerebbe a schermo una
   * partita finita senza via d'uscita.
   */
  const riferimentoDialogo = useModaleAccessibile<HTMLDivElement>(
    true,
    undefined,
    () => actionsRef.current?.querySelector('button'),
  );

  return (
    <div
      ref={riferimentoDialogo}
      className="game-end-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      <div className="game-end-card">
        {/* Result header */}
        <div className={`game-end-result ${config.className}`} data-testid="game-end-result" data-result={resultType}>
          <div className="game-end-result-icon">{config.icon}</div>
          <h2 className="game-end-result-text" id={titleId}>
            {t(RESULT_I18N_KEY[resultType])}
          </h2>
          {/*
            L'avatar sta DOPO il «vs», e non prima.
            Prima era la faccia dell'avversario a sinistra della parola, cioè nel posto
            che in «X vs Y» è di chi guarda: la riga prometteva uno scontro a due lati
            ed era invece due volte l'avversario — la sua faccia da una parte, il suo
            nome dall'altra. Ora l'avatar è attaccato al nome che etichetta.
          */}
          <div className="game-end-opponent" data-testid="game-end-opponent">
            <span className="game-end-opponent-vs" data-testid="game-end-opponent-vs">
              {t('common.vs')}
            </span>
            {/*
              `aria-hidden` sul contenitore: senza un avatar scelto il riquadro
              disegna l'INIZIALE del nome, che ora cade in mezzo, tra il «vs» e il
              nome stesso — «vs G GrandMaster42». E' un doppione decorativo della
              prima lettera, e a chi ascolta non aggiunge niente.
            */}
            <span className="game-end-opponent-avatar" aria-hidden="true">
              <UserAvatar username={opponentUsername} avatar={opponentAvatar} variant="opponent" size="sm" />
            </span>
            {canOpenProfile ? (
              <button
                type="button"
                className="game-end-opponent-link"
                onClick={() => { setShowProfile(true); }}
                aria-label={t('profile.viewProfileOf', { username: opponentUsername })}
                // Il nome si tronca con l'ellissi quando non ci sta: il titolo lo
                // tiene identificabile, come fa PlayerCard sul proprio nome.
                title={opponentUsername}
              >
                {opponentUsername}
              </button>
            ) : (
              <span className="game-end-opponent-name" title={opponentUsername}>
                {opponentUsername}
              </span>
            )}
            {isDisplayableRating(opponentRating) && (
              <span className="game-end-opponent-rating" data-testid="game-end-opponent-rating">
                {opponentRating}
              </span>
            )}
          </div>
          {reasonText && <p className="game-end-reason">{reasonText}</p>}
        </div>

        {/* Stats slot */}
        {statsSlot}

        {/* Countdown slot */}
        {countdownSlot}

        {/* Actions slot */}
        <div ref={actionsRef}>{actionsSlot}</div>
      </div>

      {showProfile && (
        <PlayerProfileOverlay
          username={opponentUsername}
          onClose={() => { setShowProfile(false); }}
        />
      )}
    </div>
  );
}
